import client from './client';
import { TimesheetType } from '../interfaces/TimesheetInterface';
import config from '../configLoader';

// Function to get employees based on pay period ID, type of timesheet, and supervisor ID
export const getEmployees = async (payPeriodID: number, type: TimesheetType, supervisorID: number, userID: number | null) => {
    try {
      // Map the enum values to their corresponding endpoints
      let endpointMap: { [key in TimesheetType]: string } = {
        [TimesheetType.Unsubmitted]: "/admin/timesheets/unsubmitted",
        [TimesheetType.ManagerPending]: "/admin/timesheets/managerPending",
        [TimesheetType.Submitted]: "/admin/timesheets/submitted",
        [TimesheetType.SuperApproved]: "/admin/timesheets/supervisorApproved",
        [TimesheetType.PayApproved]: "/admin/timesheets/payrollApproved"
      };

      // If useRequiredSuperApproval is true, use the required supervisor endpoint for submitted type
      if (config.useRequiredSuperApproval && type === TimesheetType.Submitted) {
        endpointMap[TimesheetType.Submitted] = "/admin/timesheets/submitted/requiredSupervisor";
      }

      // Use the enum to determine the appropriate endpoint
      const endpoint = endpointMap[type] || "/admin/timesheets";

      // Approval Access (WP8 / D3 / D27): the server derives the calling admin from the
      // MSAL session and scopes results server-side. We no longer send adminUserEmployeeID
      // for authorization. `userID` is retained in the signature for callers but ignored.
      // The drill-down supervisorID stays — the server validates it is within scope (403 otherwise).
      const supervisorParamName = (config.useRequiredSuperApproval && type === TimesheetType.Submitted)
        ? 'requiredSupervisorID'
        : 'supervisorID';

      return await client.get(endpoint, {
        params: {
          payPeriodID,
          [supervisorParamName]: supervisorID,
        }
      });

    } catch (error) {
      // Log any errors that occur during the fetch process
      console.error("Error fetching employees:", error);
      // Throw an error to indicate failure in fetching the employee data
      throw new Error("Failed to fetch employee data");
    }
};

// Function to get required manager pending employees
export const getRequiredManagerPendingEmployees = async (payPeriodID: number, managerEmployeeID: number, adminUserEmployeeID?: number) => {
    try {
      // Approval Access (WP8): identity is session-derived; adminUserEmployeeID is no
      // longer sent for authorization (kept in the signature for caller compatibility).
      return await client.get("/admin/timesheets/requiredManagerPending", {
        params: {
          payPeriodID,
          managerEmployeeID
        }
      });
    } catch (error) {
      console.error("Error fetching required manager pending employees:", error);
      throw new Error("Failed to fetch required manager pending employee data");
    }
};

// Function to get required approval group employees
export const getRequiredApprovalGroupEmployees = async (payPeriodID: number, approvalGroupID: number, adminUserEmployeeID?: number) => {
    try {
      // Approval Access (WP8): session-derived identity; adminUserEmployeeID not sent.
      return await client.get("/admin/timesheets/requiredApprovalGroup", {
        params: {
          payPeriodID,
          approvalGroupID
        }
      });
    } catch (error) {
      console.error("Error fetching required approval group employees:", error);
      throw new Error("Failed to fetch required approval group employee data");
    }
};

// Function to search for employees by ID or name
export const searchEmployees = async (searchString: string, payPeriodID: number, adminUserEmployeeID?: number) => {
    try {
      // Approval Access (WP8): the server scopes results from the MSAL session
      // (admin/ViewAllEmployees see all; supervisors see their scope). We no longer
      // send adminUserEmployeeID; the "admin/payroll/hr see all" opt-in is gone.
      const response = await client.get("/admin/employees/search", {
        params: {
          searchString,
          payPeriodID
        }
      });
      return response;
    } catch (error) {
      // Log any errors that occur during the search process
      console.error("Error searching employees:", error);
      // Throw an error to indicate failure in searching employees
      throw new Error("Failed to search employees");
    }
};